import { useState } from "react";
import { C } from "../constants/colors";
import { fmtMoney, fmtMetric } from "../utils/format";

function niceMax(v){
  if(v<=0) return 1;
  const exp=Math.floor(Math.log10(v));
  const base=Math.pow(10,exp);
  const n=v/base;
  const niceN=n<=1?1:n<=2?2:n<=5?5:10;
  return niceN*base;
}
function fmtCompactNumber(v){
  const av=Math.abs(v);
  if(av>=1000000) return (v/1000000).toFixed(1).replace(/\.0$/,"")+"M";
  if(av>=1000) return (v/1000).toFixed(1).replace(/\.0$/,"")+"K";
  return Math.round(v).toLocaleString();
}

// Single-series trend charts (cost / volume-weight). Per dataviz guidance: 2px line,
// r=4 end-dots with a surface ring, hairline gridlines, per-point hover tooltip —
// a single series needs no legend since the card title already names what's plotted.
export function LineChart({data,color=C.navy,isMoney=false,unit="",height=190}){
  const [hoverIdx,setHoverIdx]=useState(null);
  const w=680,h=height,padL=54,padR=16,padT=18,padB=28;
  const plotW=w-padL-padR,plotH=h-padT-padB;
  const max=niceMax(Math.max(1,...data.map(d=>d.value)));
  const n=data.length;
  const stepX=n>1?plotW/(n-1):0;
  const xAt=i=>padL+stepX*i;
  const yAt=v=>padT+plotH-(v/max)*plotH;
  const pathD=data.map((d,i)=>`${i===0?"M":"L"}${xAt(i)},${yAt(d.value)}`).join(" ");
  const ticks=[0,max/2,max];
  const fmtVal=v=>isMoney?fmtMoney(v):fmtMetric(v,unit);
  const fmtTick=t=>isMoney?`$${fmtCompactNumber(t)}`:fmtCompactNumber(t);
  return(
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{overflow:"visible",display:"block"}}>
      {ticks.map((t,i)=>(
        <g key={i}>
          <line x1={padL} x2={w-padR} y1={yAt(t)} y2={yAt(t)} stroke={C.border} strokeWidth={1}/>
          <text x={padL-8} y={yAt(t)+4} textAnchor="end" fontSize={10} fill={C.muted}>{fmtTick(t)}</text>
        </g>
      ))}
      {data.map((d,i)=><text key={i} x={xAt(i)} y={h-8} textAnchor="middle" fontSize={10} fill={C.muted}>{d.label}</text>)}
      <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"/>
      {data.map((d,i)=>(
        <g key={i}>
          <circle cx={xAt(i)} cy={yAt(d.value)} r={4} fill={color} stroke={C.white} strokeWidth={2}/>
          <rect x={xAt(i)-(stepX||plotW)/2} y={padT} width={stepX||plotW} height={plotH} fill="transparent"
            onMouseEnter={()=>setHoverIdx(i)} onMouseLeave={()=>setHoverIdx(null)}/>
        </g>
      ))}
      {hoverIdx!=null&&(
        <g>
          <line x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={padT} y2={h-padB} stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.5}/>
          <g transform={`translate(${Math.min(Math.max(xAt(hoverIdx),padL+42),w-padR-42)},${Math.max(yAt(data[hoverIdx].value)-16,padT+16)})`}>
            <rect x={-42} y={-28} width={84} height={24} rx={5} fill={C.navy}/>
            <text x={0} y={-11} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.white}>{fmtVal(data[hoverIdx].value)}</text>
          </g>
        </g>
      )}
    </svg>
  );
}

export function BarChart({data,color=C.teal,isMoney=false,unit="",height=190}){
  const [hoverIdx,setHoverIdx]=useState(null);
  const w=680,h=height,padL=54,padR=16,padT=18,padB=28;
  const plotW=w-padL-padR,plotH=h-padT-padB;
  const max=niceMax(Math.max(1,...data.map(d=>d.value)));
  const n=data.length||1;
  const bandW=plotW/n;
  const barW=Math.min(24,bandW*0.55);
  const ticks=[0,max/2,max];
  const yAt=v=>padT+plotH-(v/max)*plotH;
  const fmtVal=v=>isMoney?fmtMoney(v):fmtMetric(v,unit);
  const fmtTick=t=>isMoney?`$${fmtCompactNumber(t)}`:fmtCompactNumber(t);
  return(
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{overflow:"visible",display:"block"}}>
      {ticks.map((t,i)=>(
        <g key={i}>
          <line x1={padL} x2={w-padR} y1={yAt(t)} y2={yAt(t)} stroke={C.border} strokeWidth={1}/>
          <text x={padL-8} y={yAt(t)+4} textAnchor="end" fontSize={10} fill={C.muted}>{fmtTick(t)}</text>
        </g>
      ))}
      {data.map((d,i)=>{
        const cx=padL+bandW*i+bandW/2;
        const barH=Math.max((d.value/max)*plotH,0);
        const y=padT+plotH-barH;
        const hovered=hoverIdx===i;
        return(
          <g key={i}>
            <rect x={cx-barW/2} y={y} width={barW} height={barH} rx={3} fill={color} opacity={hovered?1:0.88}/>
            <text x={cx} y={h-8} textAnchor="middle" fontSize={10} fill={C.muted}>{d.label}</text>
            <rect x={cx-bandW/2} y={padT} width={bandW} height={plotH} fill="transparent"
              onMouseEnter={()=>setHoverIdx(i)} onMouseLeave={()=>setHoverIdx(null)}/>
          </g>
        );
      })}
      {hoverIdx!=null&&(()=>{
        const d=data[hoverIdx];
        const cx=padL+bandW*hoverIdx+bandW/2;
        const barH=Math.max((d.value/max)*plotH,0);
        const y=padT+plotH-barH;
        return(
          <g transform={`translate(${Math.min(Math.max(cx,padL+42),w-padR-42)},${Math.max(y-16,padT+16)})`}>
            <rect x={-42} y={-28} width={84} height={24} rx={5} fill={C.navy}/>
            <text x={0} y={-11} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.white}>{fmtVal(d.value)}</text>
          </g>
        );
      })()}
    </svg>
  );
}

// Multi-series trend chart (e.g. cost by stream). Per dataviz guidance for 2+ series:
// one shared y-axis, a legend (plain HTML below the chart, not SVG), and a single
// shared tooltip listing every series at the hovered x — never per-series tooltips.
export function MultiLineChart({series,isMoney=true,unit="",height=220}){
  const [hoverIdx,setHoverIdx]=useState(null);
  if(!series||series.length===0||!series[0].data?.length){
    return <div style={{fontSize:13,color:C.muted,padding:"24px 0",textAlign:"center"}}>No data to chart.</div>;
  }
  const w=680,h=height,padL=54,padR=16,padT=18,padB=28;
  const plotW=w-padL-padR,plotH=h-padT-padB;
  const labels=series[0].data.map(d=>d.label);
  const n=labels.length;
  const allValues=series.flatMap(s=>s.data.map(d=>d.value));
  const max=niceMax(Math.max(1,...allValues));
  const stepX=n>1?plotW/(n-1):0;
  const xAt=i=>padL+stepX*i;
  const yAt=v=>padT+plotH-(v/max)*plotH;
  const ticks=[0,max/2,max];
  const fmtVal=v=>isMoney?fmtMoney(v):fmtMetric(v,unit);
  const fmtTick=t=>isMoney?`$${fmtCompactNumber(t)}`:fmtCompactNumber(t);

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{overflow:"visible",display:"block"}}>
        {ticks.map((t,i)=>(
          <g key={i}>
            <line x1={padL} x2={w-padR} y1={yAt(t)} y2={yAt(t)} stroke={C.border} strokeWidth={1}/>
            <text x={padL-8} y={yAt(t)+4} textAnchor="end" fontSize={10} fill={C.muted}>{fmtTick(t)}</text>
          </g>
        ))}
        {labels.map((lbl,i)=><text key={i} x={xAt(i)} y={h-8} textAnchor="middle" fontSize={10} fill={C.muted}>{lbl}</text>)}
        {series.map(s=>{
          const pathD=s.data.map((d,i)=>`${i===0?"M":"L"}${xAt(i)},${yAt(d.value)}`).join(" ");
          return(
            <g key={s.key}>
              <path d={pathD} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"/>
              {s.data.map((d,i)=><circle key={i} cx={xAt(i)} cy={yAt(d.value)} r={3.5} fill={s.color} stroke={C.white} strokeWidth={1.5}/>)}
            </g>
          );
        })}
        {labels.map((_,i)=>(
          <rect key={i} x={xAt(i)-(stepX||plotW)/2} y={padT} width={stepX||plotW} height={plotH} fill="transparent"
            onMouseEnter={()=>setHoverIdx(i)} onMouseLeave={()=>setHoverIdx(null)}/>
        ))}
        {hoverIdx!=null&&(()=>{
          const rowH=16, boxH=14+series.length*rowH, boxW=160;
          const cx=Math.min(Math.max(xAt(hoverIdx),padL+boxW/2),w-padR-boxW/2);
          const cy=Math.max(padT+boxH/2,boxH/2+4);
          return(
            <g>
              <line x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={padT} y2={h-padB} stroke={C.muted} strokeWidth={1} strokeDasharray="3,3" opacity={0.5}/>
              <g transform={`translate(${cx},${cy})`}>
                <rect x={-boxW/2} y={-boxH/2} width={boxW} height={boxH} rx={6} fill={C.navy}/>
                <text x={-boxW/2+10} y={-boxH/2+14} fontSize={10} fontWeight={700} fill={C.teal}>{labels[hoverIdx]}</text>
                {series.map((s,si)=>(
                  <g key={s.key} transform={`translate(0,${-boxH/2+14+(si+1)*rowH})`}>
                    <line x1={-boxW/2+10} x2={-boxW/2+22} y1={-4} y2={-4} stroke={s.color} strokeWidth={3}/>
                    <text x={-boxW/2+28} y={0} fontSize={10} fill="#c8d8e8">{s.label}</text>
                    <text x={boxW/2-10} y={0} textAnchor="end" fontSize={10} fontWeight={700} fill={C.white}>{fmtVal(s.data[hoverIdx].value)}</text>
                  </g>
                ))}
              </g>
            </g>
          );
        })()}
      </svg>
      {series.length>1&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:"6px 16px",marginTop:8,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
          {series.map(s=>(
            <div key={s.key} style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:10,height:10,borderRadius:2,background:s.color,display:"inline-block"}}/>
              <span style={{fontSize:11,color:C.muted}}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Donut({segments,size=140}){
  const r=50,cx=70,cy=70; let cum=-90;
  const toR=d=>d*Math.PI/180;
  const paths=segments.filter(s=>s.value>0).map(s=>{
    const ang=(s.value/100)*360,s0=cum;cum+=ang;
    const x1=cx+r*Math.cos(toR(s0)),y1=cy+r*Math.sin(toR(s0));
    const x2=cx+r*Math.cos(toR(cum-.01)),y2=cy+r*Math.sin(toR(cum-.01));
    return <path key={s.label} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${ang>180?1:0},1 ${x2},${y2} Z`} fill={s.color} opacity={0.88}/>;
  });
  return <svg width={size} height={size} viewBox="0 0 140 140"><circle cx={cx} cy={cy} r={r} fill={C.white}/>{paths}<circle cx={cx} cy={cy} r={34} fill={C.white}/></svg>;
}

export function MiniBar({label,pctVal,color,tag}){
  return(
    <div style={{marginBottom:9}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
        <span style={{fontSize:11,color:C.text}}>{label}</span>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          {tag&&<span style={{fontSize:10,color:tag.color,fontWeight:700}}>{tag.icon} {tag.label}</span>}
          <span style={{fontSize:11,color:C.muted}}>{pctVal}%</span>
        </div>
      </div>
      <div style={{height:5,background:C.border,borderRadius:3}}>
        <div style={{height:5,borderRadius:3,background:color,width:`${pctVal}%`,transition:"width 0.6s"}}/>
      </div>
    </div>
  );
}
